import { useEffect, useState } from "react";
import { Menu, X, Play, Pause } from "lucide-react";
import { ASSETS } from "@/data";
import { useAudio } from "@/context/AudioContext";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "Over ons", href: "#over-ons" },
  { label: "Workshops", href: "#workshops" },
  { label: "Werkwijze", href: "#werkwijze" },
  { label: "Sessies & Tarieven", href: "#tarieven" },
  { label: "Muziek", href: "#muziek" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { playing, toggle } = useAudio();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-brand-cream/95 backdrop-blur-md shadow-[0_8px_30px_-22px_rgba(60,74,55,0.6)]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-10 h-[72px] flex items-center justify-between">
        <a href="#home" data-testid="logo-link" className="flex items-center">
          <img
            src={ASSETS.logo}
            alt="Freedom Serenity logo"
            className={`w-auto rounded-xl object-contain transition-all duration-500 ${scrolled ? "h-14 ring-1 ring-brand-gold/30" : "h-16 ring-1 ring-white/25"}`}
          />
        </a>

        <div className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className={`eyebrow !tracking-[0.18em] !text-[0.7rem] transition-colors hover:!text-brand-gold ${
                scrolled ? "!text-brand-ink" : "!text-white/90"
              }`}
            >
              {l.label}
            </a>
          ))}
          <button
            data-testid="nav-song-toggle"
            onClick={toggle}
            className={`grid place-items-center w-9 h-9 rounded-full transition-all ${
              scrolled ? "bg-brand-forest text-brand-cream" : "bg-white/20 text-white border border-white/40"
            } hover:scale-105`}
            aria-label="Speel lied"
          >
            {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
          </button>
        </div>

        <button
          data-testid="mobile-menu-button"
          className={`lg:hidden ${scrolled ? "text-brand-forest" : "text-white"}`}
          onClick={() => setOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={26} />
        </button>
      </nav>

      {open && (
        <div className="fixed inset-0 z-[60] bg-brand-forestDeep/97 backdrop-blur-sm flex flex-col" data-testid="mobile-menu">
          <div className="flex justify-end p-6">
            <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="text-brand-cream" aria-label="Sluit menu">
              <X size={28} />
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center gap-7">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.href.slice(1)}`}
                className="font-display text-3xl text-brand-cream hover:text-brand-goldLight transition-colors"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
